import React, { useEffect, useState } from 'react';
import { Car, Loader2, Route } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { errorMessage, isUnauthorized } from '../../lib/easerApi';
import { enqueue, shouldQueue, queuedBecause } from '../../lib/offlineQueue';
import { measuredKm, stopAcc } from '../../lib/accTracker';

const fmtKm = (km) => `${Number(km || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;

/**
 * « ACC fin » : confirme les kilomètres de l'accompagnement (mesure GPS pré-remplie, corrigeable) puis ajoute la ligne
 * PAFK sur le bon d'intervention. Hors ligne / Dolibarr indisponible : la fin est gardée sur le téléphone et rejouée.
 * onDone(result | null) est appelé après l'envoi (ou la mise en file) ; onClose() si l'Easer annule.
 */
export function AccStopDialog({ open, ev, onClose, onDone, onUnauthorized }) {
  const [km, setKm] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setKm(String(measuredKm()).replace('.', ',')); setComment(''); } }, [open]);
  if (!ev) return null;
  const measured = measuredKm();

  const submit = async (e) => {
    e.preventDefault();
    const value = parseFloat(String(km).replace(',', '.'));
    if (Number.isNaN(value) || value < 0 || value > 500) return toast.error('Indiquez un kilométrage valide (0 à 500 km)');
    setBusy(true);
    try {
      const res = await stopAcc(ev.id, { km: Math.round(value * 10) / 10, comment: comment.trim() });
      if (res.line) {
        toast.success(`Accompagnement terminé : ${fmtKm(res.session.km_billed)} ajoutés au bon d'intervention${res.line.product_linked ? ' (ligne PAFK)' : ''}`);
        if (res.warning) toast.info(res.warning, { duration: 8000 });
      } else toast.info(res.warning || 'Accompagnement terminé');
      onDone(res);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      if (shouldQueue(err)) {
        await enqueue({ type: 'acc_stop', eventId: ev.id, label: ev.label, payload: { km: Math.round(value * 10) / 10, comment: comment.trim() } });
        toast.warning(queuedBecause(err, "la fin d'accompagnement"));
        onDone(null);
        return;
      }
      toast.error(errorMessage(err, "Fin d'accompagnement impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-sm w-[95vw]" data-testid="acc-stop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-[hsl(var(--accent))]" /> Fin d'accompagnement</DialogTitle>
          <DialogDescription>{ev.client?.name || ev.label} · trajet mesuré par GPS : <strong data-testid="acc-stop-measured">{fmtKm(measured)}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="acc-km">Kilomètres à facturer (PAFK)</Label>
            <div className="relative">
              <Route className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" />
              <Input id="acc-km" inputMode="decimal" value={km} onChange={(e) => setKm(e.target.value)} className="h-11 pl-9 text-lg font-semibold" data-testid="acc-stop-km" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Corrigez si le GPS a manqué une partie du trajet. Une ligne « Participation aux frais kilométriques » sera ajoutée au bon d'intervention avec cette quantité.</p>
          </div>
          <div>
            <Label htmlFor="acc-comment">Remarque (facultatif)</Label>
            <Input id="acc-comment" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={300} placeholder="Ex. : courses + pharmacie" className="h-11" data-testid="acc-stop-comment" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" className="h-11" onClick={onClose} disabled={busy} data-testid="acc-stop-cancel">Continuer le trajet</Button>
            <Button type="submit" className="h-11 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" disabled={busy} data-testid="acc-stop-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Car className="h-4 w-4 mr-1" />} ACC fin
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
